// Points of interest (landmarks) shown on the daily interactive map.
// Each key is a `day.route_id`. Each landmark has:
//   { id, lat, lng, kind, name (trilingual), blurb (trilingual) }
// kind: "kasbah" | "gorges" | "palm" | "viewpoint" | "market" | "dunes"
//       | "music" | "hotel" | "fossils" | "airport" | "village" | "mountain"

const T = (es, en, fr) => ({ es, en, fr });

export const LANDMARK_KINDS = {
  kasbah:    { color: "#A07042", label: T("Kasbah", "Kasbah", "Kasbah") },
  gorges:    { color: "#5A7F9C", label: T("Garganta", "Gorges", "Gorges") },
  palm:      { color: "#5A6B4F", label: T("Palmeral", "Palm grove", "Palmeraie") },
  viewpoint: { color: "#D4A373", label: T("Mirador", "Viewpoint", "Mirador") },
  market:    { color: "#C16542", label: T("Mercado", "Market", "Marché") },
  dunes:     { color: "#D97742", label: T("Dunas", "Dunes", "Dunes") },
  music:     { color: "#7C8B5C", label: T("Cultura viva", "Living culture", "Culture vivante") },
  hotel:     { color: "#3A4A5F", label: T("Hotel Xaluca", "Xaluca hotel", "Hôtel Xaluca") },
  fossils:   { color: "#8B6F47", label: T("Geología", "Geology", "Géologie") },
  airport:   { color: "#5C5248", label: T("Aeropuerto", "Airport", "Aéroport") },
  village:   { color: "#A07042", label: T("Pueblo bereber", "Berber village", "Village berbère") },
  mountain:  { color: "#5A6B4F", label: T("Montaña", "Mountain", "Montagne") },
  town:      { color: "#8A5A44", label: T("Ciudad", "Town", "Ville") },
  valley:    { color: "#7C8B5C", label: T("Valle", "Valley", "Vallée") },
  camp:      { color: "#C97B3C", label: T("Vivac", "Desert camp", "Bivouac") },
  site:      { color: "#9C6B3F", label: T("Sitio histórico", "Historic site", "Site historique") },
};

export const DAY_LANDMARKS = {
  // Day 1 — Ouarzazate → Boumalne Dades
  "ad-ouarzazate-dades": [
    // Día de llegada: vuelo a Ouarzazate y traslado por carretera hasta el
    // alojamiento en Boumalne Dades. No hay visitas turísticas este día, por lo
    // que el mapa muestra únicamente el hotel.
    { id: "xaluca-dades", lat: 31.3500, lng: -5.9700, kind: "hotel",
      name: T("Hotel Xaluca Dades 4★", "Hotel Xaluca Dades 4★", "Hôtel Xaluca Dadès 4★"),
      blurb: T("Hotel-kasbah a 1.612 m de altitud con piscina climatizada, hammam y vistas panorámicas al Alto Atlas.",
               "Kasbah-hotel at 1,612 m with heated pool, hammam and panoramic High Atlas views.",
               "Hôtel-kasbah à 1 612 m, piscine chauffée, hammam et vue panoramique sur le Haut Atlas.") },
  ],

  // Day 2 — Boumalne Dades → Erfoud
  "ad-todra-erfoud": [
    { id: "boutaghrar", lat: 31.5230, lng: -6.0440, kind: "village",
      name: T("Boutaghrar (Valle de las Rosas)", "Boutaghrar (Rose Valley)", "Boutaghrar (Vallée des Roses)"),
      blurb: T("Familias bereberes viven todavía en grutas y casas trogloditas — una tradición que se mantiene desde hace siglos.",
               "Berber families still live in caves and troglodyte houses — a centuries-old tradition.",
               "Des familles berbères vivent encore dans des grottes et maisons troglodytes.") },
    { id: "tinerhir-palm", lat: 31.5147, lng: -5.5331, kind: "palm",
      name: T("Palmeral de Tinerhir", "Tinerhir palm grove", "Palmeraie de Tinerhir"),
      blurb: T("Ciudad de casas rosas envuelta por un extenso palmeral, punto de partida ideal para las Gargantas del Todra.",
               "City of pink houses wrapped by an extensive palm grove — the gateway to the Todra Gorges.",
               "Ville aux maisons roses enveloppée d'une grande palmeraie — porte des Gorges du Todra.") },
    { id: "gorges-todra", lat: 31.5847, lng: -5.5894, kind: "gorges",
      name: T("Gargantas del Todra", "Todra Gorges", "Gorges du Todra"),
      blurb: T("Paredes verticales de hasta 160 metros esculpidas durante millones de años por el río Todra. Paraíso de escaladores.",
               "Vertical walls up to 160 m carved over millions of years by the Todra river. A climbers' paradise.",
               "Parois verticales jusqu'à 160 m sculptées pendant des millions d'années par la rivière Todra.") },
    { id: "erfoud-fossils", lat: 31.4358, lng: -4.2380, kind: "fossils",
      name: T("Yacimientos de fósiles de Erfoud", "Erfoud fossil deposits", "Gisements fossiles d'Erfoud"),
      blurb: T("Fósiles marinos de más de 350 millones de años: trilobites, ammonites y peces primitivos del período Devónico.",
               "Marine fossils over 350 million years old: trilobites, ammonites and Devonian primitive fish.",
               "Fossiles marins de plus de 350 millions d'années : trilobites, ammonites, poissons primitifs.") },
    { id: "kasbah-xaluca", lat: 31.4500, lng: -4.2100, kind: "hotel",
      name: T("Kasbah Hotel Xaluca", "Kasbah Hotel Xaluca", "Kasbah Hôtel Xaluca"),
      blurb: T("Considerada única en Marruecos por sus peculiares características arquitectónicas. La «puerta del desierto».",
               "Listed as unique in Morocco for its singular architectural character. The «gateway to the desert».",
               "Considérée comme unique au Maroc pour son caractère architectural singulier.") },
  ],

  // Day 3 — Erfoud → Erg Chebbi (desert total + bivouac)
  "desert-bivouac": [
    { id: "dakar-tracks", lat: 31.2200, lng: -4.1100, kind: "village",
      name: T("Pistas del Rally Dakar", "Dakar Rally tracks", "Pistes du Rallye Dakar"),
      blurb: T("Pistas históricas que cruzaban hamadas y oueds secos durante las ediciones marroquíes del Rally Dakar (hasta 2007).",
               "Historic tracks crossing hamadas and dry wadis during the Moroccan editions of the Dakar Rally (until 2007).",
               "Pistes historiques traversant hamadas et oueds secs pendant les éditions marocaines du Dakar.") },
    { id: "canteras-fosiles", lat: 31.3500, lng: -4.1900, kind: "fossils",
      name: T("Canteras de fósiles marinos", "Marine fossil quarries", "Carrières de fossiles marins"),
      blurb: T("Yacimientos del Devónico (360 millones de años). Marruecos es uno de los mayores exportadores mundiales de fósiles devónicos.",
               "Devonian deposits (360 million years). Morocco is one of the world's largest exporters of Devonian fossils.",
               "Gisements du Dévonien (360 millions d'années).") },
    { id: "oasis-picnic", lat: 31.2000, lng: -4.0800, kind: "palm",
      name: T("Oasis del picnic", "Picnic oasis", "Oasis du pique-nique"),
      blurb: T("Almuerzo tipo «picnic» bajo las palmeras de un oasis auténtico — parada obligada antes de adentrarse en las dunas.",
               "«Picnic»-style lunch under the palms of an authentic oasis — a must-stop before entering the dunes.",
               "Déjeuner « pique-nique » sous les palmiers d'une oasis authentique.") },
    { id: "hassi-labied", lat: 31.1547, lng: -4.0017, kind: "village",
      name: T("Hassi Labied", "Hassi Labied", "Hassi Labied"),
      blurb: T("Pequeño pueblo justo a los pies del Erg Chebbi, donde cambiaremos el 4x4 por dromedarios.",
               "Small village at the foot of the Erg Chebbi where we swap the 4x4 for camels.",
               "Petit village au pied de l'Erg Chebbi : nous y échangeons le 4x4 contre des dromadaires.") },
    { id: "erg-chebbi-duna", lat: 31.0995, lng: -4.0128, kind: "dunes",
      name: T("Gran duna del Erg Chebbi", "Great Erg Chebbi dune", "Grande dune de l'Erg Chebbi"),
      blurb: T("Dunas de hasta 150 metros de altura, formadas por arena fina arrastrada por los vientos saharianos. Atardecer inolvidable.",
               "Dunes up to 150 m tall, formed by fine sand carried by Saharan winds. Unforgettable sunset.",
               "Dunes jusqu'à 150 m, formées de sable fin porté par les vents sahariens.") },
    { id: "bivouac-luxe", lat: 31.1100, lng: -4.0050, kind: "hotel",
      name: T("Bivouac de Luxe", "Bivouac de Luxe", "Bivouac de Luxe"),
      blurb: T("Haimas como las que utilizan los nómadas, con baño privado y cena bereber bajo un cielo absolutamente estrellado.",
               "Nomad-style jaimas with private bathroom and Berber dinner under an absolute starry sky.",
               "Tentes berbères avec salle de bain privée et dîner sous un ciel étoilé absolu.") },
  ],

  // Day 4 — Sunrise + Khamlia + Rissani + Erfoud relax
  "khamlia-rissani": [
    { id: "sunrise-dune", lat: 31.0995, lng: -4.0128, kind: "viewpoint",
      name: T("Amanecer sobre las dunas", "Sunrise over the dunes", "Lever du soleil sur les dunes"),
      blurb: T("La luz del alba proyecta sombras muy marcadas sobre la arena fina, creando franjas de naranja, dorado y rosa que cambian cada minuto.",
               "Dawn light casts deep shadows on the fine sand, creating stripes of orange, gold and pink that shift by the minute.",
               "La lumière de l'aube projette des ombres marquées sur le sable fin.") },
    { id: "merdani", lat: 31.1900, lng: -3.9300, kind: "village",
      name: T("Merdani (pueblo abandonado)", "Merdani (abandoned village)", "Merdani (village abandonné)"),
      blurb: T("Antiguo poblado minero abandonado a los pies del Erg Chebbi, testimonio del paso del tiempo en el desierto.",
               "Former abandoned mining village at the foot of the Erg Chebbi — testimony to the passing of time in the desert.",
               "Ancien village minier abandonné au pied de l'Erg Chebbi.") },
    { id: "khamlia-gnawa", lat: 31.0470, lng: -3.9750, kind: "music",
      name: T("Khamlia · música Gnawa", "Khamlia · Gnawa music", "Khamlia · musique Gnawa"),
      blurb: T("Pueblo de origen sudanés. Su música Gnawa es Patrimonio Cultural Inmaterial UNESCO — krakebs metálicos, tambores y ritmos hipnóticos.",
               "Village of Sudanese origin. Its Gnawa music is UNESCO Intangible Cultural Heritage — metallic krakebs, drums and hypnotic rhythms.",
               "Village d'origine soudanaise. Sa musique Gnawa est Patrimoine Culturel Immatériel UNESCO.") },
    { id: "rissani-mercado", lat: 31.2820, lng: -4.2620, kind: "market",
      name: T("Mercado de Rissani", "Rissani market", "Marché de Rissani"),
      blurb: T("Uno de los zocos más antiguos del sur de Marruecos (martes, jueves y domingos). Curioso «parking de burros» de los comerciantes.",
               "One of southern Morocco's oldest souks (Tue, Thu, Sun). The famous merchants' «donkey parking».",
               "L'un des plus anciens souks du sud du Maroc (mardi, jeudi, dimanche). Célèbre « parking d'ânes ».") },
    { id: "mirador-desierto", lat: 31.3300, lng: -4.1700, kind: "viewpoint",
      name: T("Mirador del desierto", "Desert viewpoint", "Mirador du désert"),
      blurb: T("Mirador natural para despedirnos del Erg Chebbi con una panorámica espectacular sobre el mar de dunas.",
               "Natural viewpoint to bid farewell to the Erg Chebbi with a spectacular panorama over the sea of dunes.",
               "Mirador naturel pour faire ses adieux à l'Erg Chebbi.") },
    { id: "des-dunes", lat: 31.4358, lng: -4.2380, kind: "village",
      name: T("Pizzería Des Dunes", "Des Dunes pizzeria", "Pizzeria Des Dunes"),
      blurb: T("La más auténtica pizzería bereber de Erfoud — parada obligatoria para comer tras el desierto.",
               "Erfoud's most authentic Berber pizzeria — a must-stop for lunch after the desert.",
               "La pizzeria berbère la plus authentique d'Erfoud.") },
  ],

  // Day 5 — Erfoud → Errachidia (return)
  "ad-ziz-return": [
    { id: "valle-ziz", lat: 31.6500, lng: -4.3500, kind: "palm",
      name: T("Valle del Ziz", "Ziz Valley", "Vallée du Ziz"),
      blurb: T("Uno de los mayores palmerales del norte de África — más de un millón de palmeras datileras del histórico oasis del Tafilalet.",
               "One of North Africa's largest palm groves — over a million date palms from the historic Tafilalet oasis.",
               "L'une des plus grandes palmeraies d'Afrique du Nord — plus d'un million de palmiers dattiers.") },
    { id: "mirador-ziz", lat: 31.6200, lng: -4.3450, kind: "viewpoint",
      name: T("Mirador del Valle del Ziz", "Ziz Valley viewpoint", "Mirador de la Vallée du Ziz"),
      blurb: T("Vistas espectaculares al gran valle fértil, donde se alternan kasbahs de adobe, khettaras y terrazas agrícolas.",
               "Spectacular views over the great fertile valley alternating adobe kasbahs, khettaras and agricultural terraces.",
               "Vues spectaculaires sur la grande vallée fertile.") },
    { id: "khettaras", lat: 31.7000, lng: -4.4000, kind: "fossils",
      name: T("Khettaras del Tafilalet", "Tafilalet khettaras", "Khettaras du Tafilalet"),
      blurb: T("Antiguos canales subterráneos de irrigación que han mantenido el oasis vivo desde la Edad Media.",
               "Ancient underground irrigation channels that have kept the oasis alive since the Middle Ages.",
               "Anciens canaux souterrains d'irrigation qui maintiennent l'oasis depuis le Moyen Âge.") },
    { id: "errachidia-airport", lat: 31.9314, lng: -4.4244, kind: "airport",
      name: T("Aeropuerto Moulay Ali Cherif", "Moulay Ali Cherif airport", "Aéroport Moulay Ali Cherif"),
      blurb: T("Principal punto aéreo del sur-este marroquí, conecta con Casablanca para vuelos internacionales.",
               "Main air hub of south-eastern Morocco, connecting with Casablanca for international flights.",
               "Principal point aérien du sud-est marocain.") },
  ],
};

// Helper: bounding box for an array of landmarks
export const computeLandmarkBounds = (landmarks) => {
  if (!landmarks || landmarks.length === 0) return null;
  const lats = landmarks.map((p) => p.lat);
  const lngs = landmarks.map((p) => p.lng);
  return [
    [Math.min(...lats) - 0.12, Math.min(...lngs) - 0.12],
    [Math.max(...lats) + 0.12, Math.max(...lngs) + 0.12],
  ];
};
